import { z } from 'zod';
import { AssignedLocationResponseSchema } from './location-assignment-schemas.js';
// ── Outbox state (design §5 — Dexie v1 outbox.state) ─────────────────────────
//
// Shared between apps/pwa (Dexie outbox repository, slice 3+) and any future
// consumer that needs to reason about an item's lifecycle. See design.md
// "Item state machine" for the full transition diagram (owned by slice 6).
export const OUTBOX_STATES = [
    'pending',
    'syncing',
    'synced',
    'rejected',
    'needs-auth',
    'held',
    'discarded',
];
export const OutboxStateSchema = z.enum(OUTBOX_STATES);
// ── Offline profile (design D11) ─────────────────────────────────────────────
//
// A NON-secret snapshot of "who is operating this device" persisted in the
// Dexie `kv` table so the PWA shell can render (and check-in capture can
// start, from slice 6 onward) after a full restart with zero connectivity —
// see spec "Offline session persistence". Deliberately carries NO
// access/refresh token: those stay in memory only (Zustand auth store) or in
// the httpOnly `jq4_refresh` cookie. `role` is pinned to the literal
// `'operator'` because this profile only ever exists for the PWA's
// operator-only area (`_authenticated.tsx` already redirects non-operators
// to /login before any profile would be written).
export const OfflineProfileSchema = z.object({
    userId: z.string().uuid(),
    orgId: z.string().uuid(),
    role: z.literal('operator'),
    email: z.string().email().nullable(),
    displayName: z.string(),
    locations: z.array(AssignedLocationResponseSchema),
    activeLocationId: z.string().uuid().nullable(),
    // ISO datetime string — TTL validity (30 days, design D11) is computed
    // against this by isOfflineProfileValid() in apps/pwa, not enforced here.
    savedAt: z.string().datetime(),
});
// ── Offline capture HTTP headers (pwa-offline-first Slice 5, design
// §4/D13/D15) ─────────────────────────────────────────────────────────────
//
// Consolidated here (post-rebase onto the tracker after slice 3 merged) —
// originally its own `offline-headers.ts` file, created while slice 5
// branched before this module existed on the tracker. No functional change,
// just a single home for every offline-first shared contract.
//
// `X-Captured-At` — the RAW (pre-clock-skew-correction) device-clock capture
// time, mirrors `CreateTicketRequestSchema.capturedAt` in ticket-schemas.ts
// exactly (sent as BOTH a header — for routes with no body, e.g.
// photo-upload-urls — AND the `create` route's body field, cross-checked by
// the controller for equality).
// `X-Client-Sent-At` — set FRESH by the client at HTTP send time (Date.now()),
// used together with capturedAt and the server's own clock to correct for
// client clock skew (design D15): corrected = capturedAt + (serverNow -
// clientSentAt).
export const OFFLINE_HEADERS = {
    capturedAt: 'X-Captured-At',
    clientSentAt: 'X-Client-Sent-At',
};
//# sourceMappingURL=offline-schemas.js.map